import React, { useEffect, useState } from 'react'
import { VirtualizedSelectOption, VirtualizedSelectOptionsType } from '../Select/SelectVirtualized';
import VirtualizedSelect from '../Select/SelectVirtualized';
import { nanoid } from 'nanoid';
import { useIntl } from 'react-intl';
import { Merchant, SimpleMerchant } from '../../interfaces/Merchant';
import { SimpleMeeting } from '../../interfaces/Meeting';
import { SimplePipeline } from '../../interfaces/Pipeline';
import { User } from '../../interfaces/User';
import { UserCompany } from '../../interfaces/UserCompany';
import { getSelectedValueCustomFieldMeeting, getSelectedValueCustomFieldMerchant, getSelectedValueCustomFieldPipeline } from '../../functions/helperCustomField';
import { CustomField } from '../../interfaces/Settings';
import RenderMenuList from '../RenderComponent/RenderMenuList';
import { CustomSelectMeetingOption, CustomSelectMerchantOption, CustomSelectPipelineOption } from '../Pipelines/CustomComponentSelect/CustomSelect';
import { Label } from '../../interfaces/Label';
import { useMerchantStore } from '../../stores/MerchantStore';
import useUser from '../../hooks/User';
import { useCustomeStore } from '../../stores/CustomeStore';
import moment from 'moment-timezone';
import { currencyFormatter, getSubString } from '../../functions/general';
import { SimpleProduct } from '../../interfaces/Product';

interface ItemSelectValueCustomFieldProps {
    valueSelectOptionCustomField?: {
        pipeline?: SimplePipeline[]
        meeting?: SimpleMeeting[]
        merchant?: Merchant[]
        user?: User[]
        product?: SimpleProduct[]
        // defaultMerchant?: SimpleMerchant[]
        defaultMerchant?: Merchant[]
        defaultLabel?: Label[]
        defaultParticipant?: UserCompany[]
    }
    source: 'template-merchant' | 'template-user' | 'template-pipeline' | 'template-unit' | 'template-product' | 'template-meeting',
    customFieldGeneral: CustomField
    activeTab: string
    placeholder: string
    isRequired?: boolean
    typeMultiValue?: 'default' | 'meeting' | 'pipeline' | 'merchant'
    valuesKey?: string[]
    onSelectedOptionsChange?: (customFieldId: string, options: string[]) => void
    isLoading?: boolean
}

const ItemSelectValueCustomField = ({
    valueSelectOptionCustomField,
    source,
    customFieldGeneral,
    activeTab,
    placeholder,
    isRequired = false,
    valuesKey = [],
    typeMultiValue = 'default',
    onSelectedOptionsChange,
    isLoading
}: ItemSelectValueCustomFieldProps) => {
    const [options, setOptions] = useState<VirtualizedSelectOptionsType[]>([]);
    const [currentComponent, setCurrentComponent] = useState<any>(null);
    const { merchants } = useMerchantStore();
    const user = useUser();
    const { companyId } = useCustomeStore();

    useEffect(() => { // function untuk mengambil data dari valueSelectOptionCustomField dan memasukkan ke dalam options
        if (valueSelectOptionCustomField) {
            if (activeTab === 'originalMasterLabel' && valueSelectOptionCustomField.defaultLabel) {
                const optionDefaultLabelLabel = valueSelectOptionCustomField.defaultLabel.map((option: Label) => ({
                    ...option,
                    value: option.id,
                    label: option.name!
                }));
                setOptions(optionDefaultLabelLabel || []);
                setCurrentComponent(getMenuList('default'));
            }

            if (activeTab === 'originalParticipantMeeting' && valueSelectOptionCustomField.defaultParticipant) {
                const optionDefaultParticipantMeeting = valueSelectOptionCustomField.defaultParticipant.map((option: UserCompany) => ({
                    ...option,
                    value: option.id,
                    label: option.ownerName!
                }));
                setOptions(optionDefaultParticipantMeeting || []);
                setCurrentComponent(getMenuList('default'));
            }

            if (activeTab === 'originalMerchant' && valueSelectOptionCustomField.defaultMerchant) {
                const optionDefaultMerchantSimpleMerchant = valueSelectOptionCustomField.defaultMerchant.map((option: Merchant) => ({
                    ...option,
                    value: option.id,
                    label: `${option.name} / ${option.phone}`!,
                }));
                setOptions(optionDefaultMerchantSimpleMerchant || []);
                setCurrentComponent(getMenuList('merchant'));
            }

            Object.keys(customFieldGeneral).forEach((key) => {
                if (customFieldGeneral[key].type === 'pipeline' && activeTab === key && valueSelectOptionCustomField.pipeline) {
                    const optionsSimplePipeline = valueSelectOptionCustomField.pipeline.map((option: SimplePipeline) => ({
                        ...option,
                        value: option.id,
                        label: `${option.name!} / ${option.nominal} / ${moment(option.dateCreated).format("DD MMM YYYY")}`
                    }));
                    setOptions(optionsSimplePipeline || []);
                    setCurrentComponent(getMenuList('pipeline'));
                } else if (customFieldGeneral[key].type === 'merchant' && activeTab === key && valueSelectOptionCustomField.merchant) {
                    const optionsSimpleMerchant = valueSelectOptionCustomField.merchant.map((option: SimpleMerchant) => ({
                        ...option,
                        value: option.id,
                        label: `${option.name} / ${option.phone || '-'}`!,
                    }));
                    setOptions(optionsSimpleMerchant || []);
                    setCurrentComponent(getMenuList('merchant'));
                } else if (customFieldGeneral[key].type === 'meeting' && activeTab === key && valueSelectOptionCustomField.meeting) {
                    const optionsSimpleMeeting = valueSelectOptionCustomField.meeting.map((option: SimpleMeeting) => ({
                        ...option,
                        value: option.id,
                        label: `${option.name!} / ${moment(option.dateMeet).format("DD MMM YYYY")} / ${option.meetingStatus} / ${getSubString(option.id)}`
                    }));
                    setOptions(optionsSimpleMeeting || []);
                    setCurrentComponent(getMenuList('meeting'));
                } else if (customFieldGeneral[key].type === 'user' && activeTab === key && valueSelectOptionCustomField.user) {
                    const optionsSimpleUser = valueSelectOptionCustomField.user.map((option: User) => ({
                        ...option,
                        value: option.id,
                        label: option.name!
                    }));
                    setOptions(optionsSimpleUser || []);
                    setCurrentComponent(getMenuList('user'));
                } else if (customFieldGeneral[key].type === 'product' && activeTab === key && valueSelectOptionCustomField.product) {
                    const optionsSimpleProduct = valueSelectOptionCustomField.product.map((option: SimpleProduct) => ({
                        ...option,
                        value: option.id,
                        label: option.name!
                    }));
                    setOptions(optionsSimpleProduct || []);
                    setCurrentComponent(getMenuList('product'));
                }
            });
        }

    }, [valueSelectOptionCustomField, activeTab, customFieldGeneral]);

    useEffect(() => {
        setOptions([]);
    }, [activeTab]);

    const handleChangeSelectedOptions = (selectedOptions: VirtualizedSelectOption[]) => {
        if (onSelectedOptionsChange) {
            const optionValues = selectedOptions.map(option => option.value as string);
            onSelectedOptionsChange(activeTab, optionValues);
        }
    };

    const getMenuList = (type: string) => {
        if (type === 'pipeline') {
            return {
                Option: CustomSelectPipelineOption,
            };
        } else if (type === 'meeting') {
            return {
                Option: CustomSelectMeetingOption
            };
        } else if (type === 'merchant') {
            return {
                Option: (props: any) => CustomSelectMerchantOption({
                    merchants,
                    option: props.option,
                    user,
                    companyId
                })
            };
        } else {
            return {
                MenuList: RenderMenuList
            };
        }
    };

    return (
        <div className="input-group mb-3">
            <VirtualizedSelect
                options={options}
                isLoading={isLoading ?? false}
                isDisabled={isLoading ?? false}
                className="w-100"
                placeholder={placeholder}
                value={options.filter(option => valuesKey.includes(option.value as string)) as VirtualizedSelectOption[]}
                onChange={handleChangeSelectedOptions}
                components={{
                    Option: currentComponent?.Option,
                }}
                typeMultiValue={typeMultiValue}
                isMultiValue
                isMulti
                closeMenuOnSelect={false}
                // isClearable
                // defaultComponentConfig={{
                //     isMultiValuesMaxHeight: '150px'
                // }}
            />
        </div>
    );
};

export default ItemSelectValueCustomField;